import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DeleteModel from '../Common/DeleteModel';
import { useAuth } from '../../Context/AuthContext';
import PracticeEmployerModal from './PracticeEmployerModal';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { saveProviderEmployer, getAllProviderEmployers, deleteProviderEmployer } from '../../Services/ProviderService';

const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const getOrganizationIdFromStorage = () => {
  try {
    return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read organization ID from localStorage:', error);
    return null;
  }
};

export default function PracticeEmployer({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [items, setItems] = useState(initialData?.employers || []);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingEmployer, setSavingEmployer] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, item: null, isLoading: false });
  const loadEmployers = async () => {
    if (!token || !providerId) return [];
    setLoading(true);
    try {
      const response = await getAllProviderEmployers(token, providerId);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setItems(refreshed);
        return refreshed;
      } else if (!response?.is_success && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        //toast.error(response?.message || 'Failed to load provider employers');
      }
      return [];
    } catch (error) {
      console.error('Error loading provider employers:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !providerId) return;
    if (initialData?.employers?.length > 0) return;

    let isMounted = true;
    const load = async () => {
      if (!isMounted) return;
      await loadEmployers();
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [token, providerId, initialData]);

  const handleAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setShowModal(true);
  };

  const handleSave = async (values) => {
    const organizationId = getOrganizationIdFromStorage();
    
    const payload = {
      ...values,
      provider_id: providerId || values.provider_id,
      organization_id: organizationId || values.organization_id,
      type: values.id ? 'update' : 'create',
    };

    if (!token) {
      toast.error('Unable to save employer without authentication.');
      return;
    }

    if (!providerId) {
      const updated = [...items];
      if (editingIndex !== null) {
        updated[editingIndex] = values;
      } else {
        updated.push(values);
      }
      setItems(updated);
      setShowModal(false);
      setEditingIndex(null);
      toast.success('Provider ID missing. Employer saved locally until provider is created.');
      return;
    }

    setSavingEmployer(true);
    try {
      const response = await saveProviderEmployer(token, payload);
      if (response?.is_success) {
        const refreshed = await loadEmployers();
        if (!refreshed) {
          toast.error('Failed to refresh employer records after save');
          return;
        }
        setShowModal(false);
        setEditingIndex(null);
        toast.success(response?.message || 'Employer saved successfully');
      } else if (!response?.is_success && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save employer');
      }
    } catch (error) {
      console.error('Error saving provider employer:', error);
      toast.error(error?.message || 'Failed to save employer');
    } finally {
      setSavingEmployer(false);
    }
  };

  const openDeleteModal = (index) => {
    setDeleteModal({ isOpen: true, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDelete(deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const originalItems = [...items];

    if (token && item && (item.id || item.Id || item.employer_id)) {
      try {
        const id = item.id || item.Id || item.employer_id;
        const resp = await deleteProviderEmployer(token, id);
        if (resp?.is_success) {
          const refreshed = await loadEmployers();
          const nextItems = Array.isArray(refreshed) ? refreshed : items.filter((_, i) => i !== index);
          setItems(nextItems);
          if (onSaveSuccess) onSaveSuccess(nextItems);
          toast.success(resp?.message || 'Employer deleted successfully');
          return;
        } else if (!resp?.is_success && resp?.status_code === 500) {
          setItems(originalItems);
          if (onSaveSuccess) onSaveSuccess(originalItems);
          toast.error('Internal Server Error. Please Contact Support Team.');
          return;
        } else {
          console.error('Failed to delete employer:', resp);
          setItems(originalItems);
          if (onSaveSuccess) onSaveSuccess(originalItems);
          toast.error(resp?.message || 'Failed to delete employer');
          return;
        }
      } catch (error) {
        console.error('Error deleting employer:', error);
        setItems(originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
        toast.error('Failed to delete employer');
        return;
      }
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Practice Employers</h6>
          <button type="button" className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2" onClick={handleAdd}>
            <MdAdd size={18} /> Add
          </button>
        </div>

         {loading ?(
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : items.length === 0 ? (
                    <div className="text-center py-4 text-muted small">
                      No practice employers added yet. Click "Add" to create one.
                    </div>
                  ) : (
        
                  <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3 mb-3">
                    {
                      items.map((it, idx) => (
                        <React.Fragment key={idx}>
                      <div className="col">
                        <div className="card h-100 border-0 shadow-lg rounded-4">
                          <div className="card-body">
        
                            <div className="text-muted small mb-2">
                              Practice Name
                            </div>
        
                            <div className="fw-semibold text-dark">
                              {it.employer_name || it.w9_practice_name || 'Unnamed'}
                            </div>
        
                          </div>
                        </div>
                      </div>
        
        
          
                      <div className="col">
                        <div className="card h-100 border-0 shadow-lg rounded-4">
                          <div className="card-body">
        
                            <div className="text-muted small mb-2">
                              Office Type
                            </div>
        
                            <div className="fw-semibold text-dark">
                             {it.office_type_name || it.office_type || 'N/A'}
                            </div>
        
                          </div>
                        </div>
                      </div>
        
        
        
                      <div className="col">
                        <div className="card h-100 border-0 shadow-lg rounded-4">
                          <div className="card-body">
        
                            <div className="text-muted small mb-2">
                              Start Date
                            </div>
        
                            <div className="fw-semibold text-dark">
                            {it.start_date || 'N/A'}
                            </div>
        
                          </div>
                        </div>
                      </div>



                      <div className="col">
                        <div className="card h-100 border-0 shadow-lg rounded-4">
                          <div className="card-body">
        
                            <div className="text-muted small mb-2">
                              End Date
                            </div>
        
                            <div className="fw-semibold text-dark">
                             {it.end_date || 'N/A'}
                            </div>
        
                          </div>
                        </div>
                      </div>
        
        
        
                      <div className="col">
                        <div className="card h-100 border-0 shadow-lg rounded-4">
                          <div className="card-body">
        
                            <div className="text-muted small mb-2">
                              Actions
                            </div>
        
                            <div className="d-flex gap-2">
        
                              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(idx)}><MdEdit size={16} /></button>
                              <button type="button" className="btn btn-sm btn-danger" onClick={() => openDeleteModal(idx)}><MdDelete size={16} /></button>
        
                            </div>
        
                          </div>
                        </div>
                      </div> 
                        </React.Fragment>
        
                      ))}
                     
        
                    </div>
        
                    )}


        <PracticeEmployerModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingIndex(null); }}
          onSave={handleSave}
          initialData={editingIndex !== null ? items[editingIndex] : null}
          isLoading={savingEmployer}
        />
      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="practice employer"
        itemName={deleteModal.item ? (deleteModal.item.employer_name || deleteModal.item.w9_practice_name || 'this practice employer') : 'this practice employer'}
        description="This will permanently delete this practice employer. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </>
  );
}

